import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Link2, Plus, RefreshCw, Copy, Check, AlertCircle, CheckCircle2,
  Loader2, Bot, Activity, Settings, Trash2, Play, ExternalLink,
  Key, Globe, Shield, Zap, TrendingUp, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSuperConnect() {
  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('platforms');
  
  // Connection modal state
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectStep, setConnectStep] = useState(1);
  const [credentials, setCredentials] = useState({
    platform_url: '',
    platform_code: '',
    api_key: ''
  });
  const [validationResult, setValidationResult] = useState(null);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [enableOAuth, setEnableOAuth] = useState(true);
  const [enableWebhooks, setEnableWebhooks] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [identityRes, platformsRes, statusRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/super-connect/identity`, { headers }),
        fetch(`${API_URL}/api/super-connect/platforms`, { headers }),
        fetch(`${API_URL}/api/super-connect/status`, { headers }),
        fetch(`${API_URL}/api/super-connect/logs?limit=50`, { headers })
      ]);

      if (identityRes.ok) setIdentity(await identityRes.json());
      if (platformsRes.ok) setPlatforms((await platformsRes.json()).platforms || []);
      if (statusRes.ok) setConnectionStatus(await statusRes.json());
      if (logsRes.ok) setLogs((await logsRes.json()).logs || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copiado al portapapeles');
  };

  const validateCredentials = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/super-connect/connect/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();
      if (response.ok && data.valid) {
        setValidationResult(data);
        setSelectedEndpoints(data.recommended_endpoints || []);
        setConnectStep(2);
        
        // Get AI analysis
        analyzeWithAI();
      } else {
        toast.error(data.detail || 'Error al validar credenciales');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setConnecting(false);
    }
  };

  const analyzeWithAI = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/super-connect/ai-agent/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setAiAnalysis(data.ai_analysis);
      }
    } catch (error) {
      console.error('AI analysis error:', error);
    }
  };

  const establishConnection = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/super-connect/connect/establish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          credentials,
          selected_endpoints: selectedEndpoints,
          enable_oauth_sync: enableOAuth,
          enable_webhooks: enableWebhooks
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`Conectado exitosamente a ${data.platform_name}`);
        setShowConnectModal(false);
        resetConnectionForm();
        loadData();
      } else {
        toast.error(data.detail || 'Error al establecer conexión');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setConnecting(false);
    }
  };

  const resetConnectionForm = () => {
    setConnectStep(1);
    setCredentials({ platform_url: '', platform_code: '', api_key: '' });
    setValidationResult(null);
    setSelectedEndpoints([]);
    setAiAnalysis(null);
  };

  const disconnectPlatform = async (platformId) => {
    if (!window.confirm('¿Estás seguro de desconectar esta plataforma?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/super-connect/platforms/${platformId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Plataforma desconectada');
        loadData();
      }
    } catch (error) {
      toast.error('Error al desconectar');
    }
  };

  const testPlatform = async (platformId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/super-connect/ai-agent/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ platform_id: platformId })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Test completado: ${data.tests_passed}/${data.tests_total} exitosos`);
        loadData();
      }
    } catch (error) {
      toast.error('Error en test');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-connect-page">
      {/* Header with Platform Identity */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-6 bg-gradient-to-r from-primary/10 to-orange-500/10 rounded-2xl border">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Super Admin Connect</h1>
              <p className="text-muted-foreground">Conecta y vincula plataformas externas</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {identity && (
            <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-lg border">
              <Key className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-mono font-bold text-primary">{identity.platform_code}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => copyToClipboard(identity.platform_code, 'code')}
              >
                {copiedField === 'code' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          )}
          
          <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="connect-platform-btn">
                <Plus className="h-4 w-4" />
                Vincular Plataforma
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary" />
                  Vincular Nueva Plataforma
                </DialogTitle>
                <DialogDescription>
                  {connectStep === 1 ? 'Ingresa las credenciales de la plataforma a vincular' : 'Configura la conexión'}
                </DialogDescription>
              </DialogHeader>

              {connectStep === 1 ? (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="platform_url">URL de la Plataforma</Label>
                    <Input
                      id="platform_url"
                      placeholder="https://ejemplo.com"
                      value={credentials.platform_url}
                      onChange={(e) => setCredentials({ ...credentials, platform_url: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="platform_code">Código Único de Plataforma</Label>
                    <Input
                      id="platform_code"
                      placeholder="OK30031234567"
                      value={credentials.platform_code}
                      onChange={(e) => setCredentials({ ...credentials, platform_code: e.target.value })}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">Formato: OK + 4 dígitos (día/mes) + 7 dígitos aleatorios</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="api_key">API Key</Label>
                    <Input
                      id="api_key"
                      type="password"
                      placeholder="okr_xxxxxxxxxxxxx"
                      value={credentials.api_key}
                      onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  {/* Platform Info */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Plataforma Verificada
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Nombre:</span>
                          <p className="font-medium">{validationResult?.platform_info?.name}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Versión:</span>
                          <p className="font-medium">{validationResult?.platform_info?.version}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* AI Analysis */}
                  {aiAnalysis && (
                    <Card className="border-primary/30 bg-primary/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Bot className="h-4 w-4 text-primary" />
                          Análisis del Agente IA
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-line">{aiAnalysis}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Endpoints Selection */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Endpoints Disponibles</CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedEndpoints(
                            selectedEndpoints.length === validationResult?.available_endpoints?.length 
                              ? [] 
                              : validationResult?.available_endpoints?.map(e => e.id) || []
                          )}
                        >
                          {selectedEndpoints.length === validationResult?.available_endpoints?.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-48">
                        <div className="space-y-2">
                          {validationResult?.available_endpoints?.map((endpoint) => (
                            <div key={endpoint.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedEndpoints.includes(endpoint.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedEndpoints([...selectedEndpoints, endpoint.id]);
                                  } else {
                                    setSelectedEndpoints(selectedEndpoints.filter(e => e !== endpoint.id));
                                  }
                                }}
                              />
                              <div className="flex-1">
                                <span className="text-sm font-medium">{endpoint.id}</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  {endpoint.method} {endpoint.path}
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs">{endpoint.category}</Badge>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Options */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Sincronización OAuth SSO</p>
                            <p className="text-xs text-muted-foreground">Vincular cuentas de usuarios</p>
                          </div>
                        </div>
                        <Switch checked={enableOAuth} onCheckedChange={setEnableOAuth} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Webhooks Automáticos</p>
                            <p className="text-xs text-muted-foreground">Sincronización en tiempo real</p>
                          </div>
                        </div>
                        <Switch checked={enableWebhooks} onCheckedChange={setEnableWebhooks} />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <DialogFooter>
                {connectStep === 1 ? (
                  <Button 
                    onClick={validateCredentials} 
                    disabled={connecting || !credentials.platform_url || !credentials.platform_code || !credentials.api_key}
                  >
                    {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Validar y Continuar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setConnectStep(1)}>Atrás</Button>
                    <Button onClick={establishConnection} disabled={connecting || selectedEndpoints.length === 0}>
                      {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Conectar
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Platform Credentials Card */}
      {identity && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-4 w-4" />
              Credenciales de esta Plataforma
            </CardTitle>
            <CardDescription>Comparte estas credenciales para que otras plataformas se conecten a OkReserva</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">URL de Plataforma</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-muted px-2 py-1 rounded truncate">{identity.platform_url}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(identity.platform_url, 'url')}>
                    {copiedField === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Código Único</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-muted px-2 py-1 rounded font-mono font-bold text-primary">{identity.platform_code}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(identity.platform_code, 'code2')}>
                    {copiedField === 'code2' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-muted px-2 py-1 rounded truncate">{identity.api_key_masked}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(identity.api_key, 'apikey')}>
                    {copiedField === 'apikey' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="platforms" className="gap-2">
            <Globe className="h-4 w-4" />
            Plataformas Conectadas
          </TabsTrigger>
          <TabsTrigger value="agent" className="gap-2">
            <Bot className="h-4 w-4" />
            Agente IA de Conexión
          </TabsTrigger>
          <TabsTrigger value="status" className="gap-2">
            <Activity className="h-4 w-4" />
            Panel de Estado
          </TabsTrigger>
        </TabsList>

        {/* Connected Platforms Tab */}
        <TabsContent value="platforms" className="space-y-4">
          {platforms.length === 0 ? (
            <Card className="py-12 text-center">
              <CardContent>
                <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No hay plataformas conectadas</h3>
                <p className="text-muted-foreground mb-4">Conecta tu primera plataforma para comenzar a sincronizar datos</p>
                <Button onClick={() => setShowConnectModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Vincular Plataforma
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {platforms.map((platform) => (
                <Card key={platform.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Globe className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{platform.platform_name}</CardTitle>
                          <p className="text-xs text-muted-foreground font-mono">{platform.platform_code}</p>
                        </div>
                      </div>
                      <Badge variant={platform.status === 'connected' ? 'default' : platform.status === 'error' ? 'destructive' : 'secondary'}>
                        {platform.status === 'connected' ? 'Conectado' : platform.status === 'error' ? 'Error' : platform.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Endpoints:</span>
                        <span>{platform.selected_endpoints?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">OAuth:</span>
                        <span>{platform.oauth_sync_enabled ? 'Activo' : 'Inactivo'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Webhooks:</span>
                        <span>{platform.webhooks_enabled ? 'Activo' : 'Inactivo'}</span>
                      </div>
                    </div>
                    
                    {platform.last_ping && (
                      <div className="flex items-center gap-2 text-xs">
                        {platform.last_ping.online ? (
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-500" />
                        )}
                        <span className="text-muted-foreground">
                          {platform.last_ping.online ? `${Math.round(platform.last_ping.response_time_ms)}ms` : 'Offline'}
                        </span>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => testPlatform(platform.id)}>
                        <Play className="h-3 w-3 mr-1" />
                        Test
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={platform.platform_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => disconnectPlatform(platform.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* AI Agent Tab */}
        <TabsContent value="agent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-primary" />
                Agente IA de Conexión
              </CardTitle>
              <CardDescription>
                El agente IA analiza plataformas, descubre endpoints y configura conexiones automáticamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Globe className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{platforms.length}</p>
                        <p className="text-sm text-muted-foreground">Plataformas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <Zap className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{connectionStatus?.sync_stats?.total || 0}</p>
                        <p className="text-sm text-muted-foreground">Sincronizaciones</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{connectionStatus?.sync_stats?.success_rate || 100}%</p>
                        <p className="text-sm text-muted-foreground">Tasa de Éxito</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Bot className="h-4 w-4" />
                  Capacidades del Agente
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Descubrimiento automático de endpoints y APIs</li>
                  <li>• Análisis de compatibilidad OAuth SSO</li>
                  <li>• Configuración automática de webhooks</li>
                  <li>• Pruebas de conexión y diagnóstico de errores</li>
                  <li>• Recomendaciones de configuración óptima</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Status Panel Tab */}
        <TabsContent value="status" className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-500">{connectionStatus?.status_breakdown?.connected || 0}</p>
                    <p className="text-sm text-muted-foreground">Conectadas</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-red-500">{connectionStatus?.status_breakdown?.error || 0}</p>
                    <p className="text-sm text-muted-foreground">Con Error</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-red-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-yellow-500">{connectionStatus?.status_breakdown?.requires_reauth || 0}</p>
                    <p className="text-sm text-muted-foreground">Re-auth</p>
                  </div>
                  <RefreshCw className="h-8 w-8 text-yellow-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{connectionStatus?.total_platforms || 0}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                  <Globe className="h-8 w-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Incidents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                Incidencias Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {connectionStatus?.recent_incidents?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hay incidencias recientes</p>
              ) : (
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {connectionStatus?.recent_incidents?.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {log.level === 'error' ? (
                          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{log.message}</p>
                          <p className="text-xs text-muted-foreground">
                            {log.event_type} • {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* All Logs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Registro de Actividad</CardTitle>
                <Button variant="outline" size="sm" onClick={loadData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <div className="space-y-1">
                  {logs.map((log, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 text-sm hover:bg-muted/50 rounded">
                      <Badge variant={log.level === 'error' ? 'destructive' : log.level === 'warning' ? 'secondary' : 'outline'} className="text-xs">
                        {log.level}
                      </Badge>
                      <span className="flex-1 truncate">{log.message}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
